import { FC } from "react";
import {
  PrimaryButton,
  SecondaryButton,
  NumericField,
  EnumSingleSelectFieldV2,
  LoadingV2,
} from "@ucl/library";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { PersonalAccountBequest } from "@/types";
import { usePersonalAccountBequests } from "@/hooks/usePersonalAccountBequests";
import "./PersonalAccountBeneficiaries.scss";

export interface PersonalAccountBeneficiaryBequestsProps {
  financialApplicationId: string;
  personalAccountBeneficiaryId: string;
  readOnly?: boolean;
}

const BequestItemSkeleton: FC = () => (
  <div className="bequest-item">
    <div className="bequest-item-content">
      <div className="bequest-item-info">
        <LoadingV2 width="200px" height="20px" />
      </div>
      <div className="bequest-percentage-display">
        <LoadingV2 width="120px" height="16px" />
      </div>
      <LoadingV2 width="80px" height="32px" />
      <LoadingV2 width="80px" height="32px" />
    </div>
  </div>
);

export const PersonalAccountBeneficiaryBequests: FC<PersonalAccountBeneficiaryBequestsProps> = (
  props
) => {
  const {
    beneficiary,
    selectedAccountId,
    setSelectedAccountId,
    bequestPercentage,
    setBequestPercentage,
    isLoading,
    handleAddBequest,
    handleRemoveBequest,
    handleUpdateBequest,
    getAvailableProducts,
    getBequestPercentages,
    products,
    isAddingBequest,
    isUpdatingBequest,
    isRemovingBequest,
  } = usePersonalAccountBequests(props.financialApplicationId, props.personalAccountBeneficiaryId);

  if (isLoading) {
    return <LoadingV2 />;
  }

  const availableProducts = getAvailableProducts();

  // Create options for the dropdown
  const accountOptions = availableProducts.map((depositApp) => {
    const percentages = getBequestPercentages(depositApp.id);
    const product = products.find(p => p.id === depositApp.financialProductId);
    const productName = product ? product.productName : `Account ${depositApp.id}`;
    return {
      label: `${productName} (${percentages.remaining.toFixed(2)}% Remaining)`,
      value: depositApp.id,
    };
  });

  return (
    <div className="personal-account-beneficiary-bequests">
      <div className="bequests-header">
        <h4>Beneficiary Designations</h4>
        {beneficiary?.personalAccountBequests && beneficiary.personalAccountBequests.length > 0 && (
          <span className="bequests-count">
            {beneficiary.personalAccountBequests.length} beneficiary designation
            {beneficiary.personalAccountBequests.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      <div className="bequests-list">
        {/* Existing Bequests */}
        {beneficiary?.personalAccountBequests && beneficiary.personalAccountBequests.map((bequest: PersonalAccountBequest) => {
          const isUpdating = isUpdatingBequest(bequest.depositApplicationId);
          const isRemoving = isRemovingBequest(bequest.depositApplicationId);
          
          // Show skeleton if updating or removing
          if (isUpdating || isRemoving) {
            return <BequestItemSkeleton key={bequest.depositApplicationId} />;
          }

          const percentages = getBequestPercentages(bequest.depositApplicationId);
          const product = products.find(p => p.id === bequest.financialProductId);
          const productName = product ? product.productName : bequest.financialProductName;
          return (
            <div key={bequest.depositApplicationId} className="bequest-item">
              <div className="bequest-item-content">
                <div className="bequest-item-info">
                  <span className="bequest-product">{productName}</span>
                </div>
                <div className="bequest-percentage-display">
                  <span className="percentage-available">
                    {percentages.remaining.toFixed(2)}% Available
                  </span>
                </div>
                {!props.readOnly ? (
                  <NumericField
                    value={bequest.percentageBequeathed}
                    onSubmit={(value) =>
                      handleUpdateBequest(bequest.depositApplicationId, value || 0)
                    }
                    min={1}
                    max={percentages.remaining + percentages.thisBeneficiaryBequeathed}
                    placeholder="Enter percentage..."
                    showIncrementButtons={false}
                    width="80px"
                    decimals={2}
                    isPercentage={true}
                  />
                ) : (
                  <span className="bequest-percentage">{bequest.percentageBequeathed?.toFixed(2)}%</span>
                )}
                {!props.readOnly && (
                  <SecondaryButton
                    minimal={true}
                    iconProps={{ icon: faTrash }}
                    cssClass="subtle danger-button"
                    onClick={() => handleRemoveBequest(bequest.depositApplicationId)}
                  />
                )}
              </div>
            </div>
          );
        })}

        {/* Add New Bequest */}
        {!props.readOnly && availableProducts.length > 0 && (
          <div className="add-bequest-item">
            <div className="bequest-item-content">
              <div className="bequest-item-info">
                <EnumSingleSelectFieldV2
                  type="EnumSingleSelect"
                  value={selectedAccountId}
                  optionValues={accountOptions}
                  onSubmit={(value) => setSelectedAccountId(value?.toString() || "")}
                  placeholder="Choose account..."
                  showClearButton={true}
                />
              </div>
              {selectedAccountId && (
                <div className="bequest-percentage-display">
                  <span className="percentage-available">
                    {getBequestPercentages(selectedAccountId).remaining.toFixed(2)}% Available
                  </span>
                </div>
              )}
              {!selectedAccountId && <div className="bequest-percentage-display" />}
              <NumericField
                value={bequestPercentage}
                onSubmit={(value) => setBequestPercentage(value || 0)}
                max={selectedAccountId ? getBequestPercentages(selectedAccountId).remaining : 100}
                placeholder="Enter percentage..."
                showIncrementButtons={false}
                isPercentage={true}
                decimals={2}
                width="80px"
              />
              <PrimaryButton
                text="Add"
                iconProps={{ icon: faPlus }}
                onClick={handleAddBequest}
                disabled={!selectedAccountId || (bequestPercentage || 0) <= 0 || isAddingBequest}
              />
            </div>
          </div>
        )}

        {/* Show skeleton for adding bequest if in progress */}
        {isAddingBequest && (
          <BequestItemSkeleton />
        )}
      </div>

      {!props.readOnly && availableProducts.length === 0 && (
        <div className="no-available-products">
          <p>
            {beneficiary?.personalAccountBequests && beneficiary.personalAccountBequests.length > 0
              ? "All available accounts have already been bequeathed."
              : "No accounts available for bequeathing."}
          </p>
        </div>
      )}
    </div>
  );
};
